import { defineStore } from 'pinia'
import { ref, computed } from 'vue'
import api from '@/api/client'
import { REFRESH_CSRF_COOKIE_NAME } from '@/api/refreshCsrf'
import { useMyTenantsStore } from '@/stores/myTenants'

export interface AuthUser {
  id: number
  email: string
  first_name: string | null
  last_name: string | null
  company_name: string | null
  company_size: string | null
  referral_source: string | null
}

interface AuthUserPayload {
  id: number
  email: string
  first_name: string | null
  last_name: string | null
  company_name?: string | null
  company_size?: string | null
  referral_source?: string | null
}

interface AuthSessionPayload {
  user: AuthUserPayload
  tenant_id: number | null
  roles?: string[]
}

function normalizeUser(data: AuthUserPayload): AuthUser {
  return {
    id: data.id,
    email: data.email,
    first_name: data.first_name,
    last_name: data.last_name,
    company_name: data.company_name ?? null,
    company_size: data.company_size ?? null,
    referral_source: data.referral_source ?? null,
  }
}

export const useAuthStore = defineStore('auth', () => {
  const user = ref<AuthUser | null>(null)
  const tenantId = ref<number | null>(null)
  const roles = ref<string[]>([])
  const bootstrapped = ref(false)

  const isAuthenticated = computed(() => !!user.value)

  /**
   * False only for a signed-in account that still owes the company profile.
   *
   * The router guard uses this to hold the account on `/complete-profile`, so the check
   * must never fire for a signed-out visitor (`user === null`).
   */
  const profileComplete = computed(
    () =>
      !!user.value &&
      !!user.value.company_name &&
      !!user.value.company_size &&
      !!user.value.referral_source,
  )

  async function bootstrap() {
    try {
      const { data } = await api.get<
        AuthUserPayload & { tenant_id: number | null; roles?: string[] }
      >('/me')
      user.value = normalizeUser(data)
      tenantId.value = data.tenant_id
      roles.value = data.roles || []
    } catch {
      user.value = null
      tenantId.value = null
      roles.value = []
      useMyTenantsStore().reset()
    } finally {
      bootstrapped.value = true
    }
    if (user.value) {
      try {
        await useMyTenantsStore().fetchMyTenants()
      } catch {
        /* Non-fatal: navigation guard can refetch /me/tenants. */
      }
    }
  }

  /**
   * Store the session returned by either sign-in route and load the tenant list.
   *
   * A freshly registered account has no tenant yet (`tenant_id: null`), which is what the
   * router guard uses to send the user to onboarding.
   */
  async function applySession(data: AuthSessionPayload) {
    user.value = normalizeUser(data.user)
    tenantId.value = data.tenant_id
    roles.value = data.roles || []
    try {
      await useMyTenantsStore().fetchMyTenants()
    } catch {
      /* Non-fatal: navigation guard can refetch /me/tenants. */
    }
  }

  /**
   * Re-mint the access token and reload the session.
   *
   * The tenant (`tnt`) claim is baked into the token, so a workspace created after sign-in
   * is invisible to tenant-scoped routes until the token is rotated.
   */
  async function refresh() {
    await api.post(
      '/public/auth/refresh',
      {},
      { skipAuthRefresh: true, xsrfCookieName: REFRESH_CSRF_COOKIE_NAME },
    )
    await bootstrap()
  }

  async function login(payload: { email: string; password: string }) {
    const { data } = await api.post<AuthSessionPayload>('/public/auth/login', payload)
    await applySession(data)
  }

  /**
   * Sign in with a Google Identity Services ID token. Never creates an account.
   *
   * Rejects with the API error; callers branch on `response.data.code`
   * (`google_account_not_found`, `google_link_required`, ...).
   */
  async function googleSignIn(idToken: string) {
    const { data } = await api.post<AuthSessionPayload>('/public/auth/google/login', {
      id_token: idToken,
    })
    await applySession(data)
  }

  /**
   * Create an account from a Google ID token. Never signs in an existing account.
   *
   * The company profile is collected by the mandatory post-signup step, never here.
   */
  async function googleRegister(idToken: string) {
    const { data } = await api.post<AuthSessionPayload>('/public/auth/google/register', {
      id_token: idToken,
    })
    await applySession(data)
  }

  /** Finish linking Google to a password account once the user confirms the password. */
  async function googleLink(linkToken: string, password: string) {
    const { data } = await api.post<AuthSessionPayload>('/public/auth/google/link', {
      link_token: linkToken,
      password,
    })
    await applySession(data)
  }

  async function logout() {
    await api.post('/public/auth/logout')
    user.value = null
    tenantId.value = null
    roles.value = []
    useMyTenantsStore().reset()
  }

  return {
    user,
    tenantId,
    roles,
    bootstrapped,
    isAuthenticated,
    profileComplete,
    bootstrap,
    applySession,
    refresh,
    login,
    googleSignIn,
    googleRegister,
    googleLink,
    logout,
  }
})
